import { Link } from "react-router-dom";

const taxItems = [
  {
    icon: "ri-money-pound-circle-line",
    title: "Income Tax on Distributions",
    desc: "Monthly distributions received by investors are likely to be classified as investment income and may be subject to UK Income Tax. Basic rate taxpayers pay 20%, higher rate 40%, and additional rate taxpayers 45%. The applicable rate depends on your total taxable income in each tax year.",
    tag: "Income Tax",
    tagColor: "#C9A84C",
  },
  {
    icon: "ri-line-chart-line",
    title: "Capital Gains Tax at Maturity",
    desc: "Where investor capital is returned at maturity, all or part of the repayment may give rise to a Capital Gains Tax (CGT) liability depending on how the investment is structured in your personal tax position. The annual CGT exempt amount should be considered when planning your exit.",
    tag: "CGT",
    tagColor: "#4CAF8A",
  },
  {
    icon: "ri-safe-line",
    title: "Tax-Efficient Wrappers (SIPP / SSAS)",
    desc: "Certain qualified investors may be able to hold this investment within a Self-Invested Personal Pension (SIPP) or Small Self-Administered Scheme (SSAS), potentially sheltering returns from Income Tax and CGT. Eligibility depends on individual scheme rules and pension regulations. Independent pension advice is recommended.",
    tag: "Pension Wrapper",
    tagColor: "#6B8F9F",
  },
  {
    icon: "ri-calendar-2-line",
    title: "Annual Allowances & Exemptions",
    desc: "Investors should consider their Personal Savings Allowance (£500 for higher rate taxpayers, £1,000 for basic rate), the CGT Annual Exempt Amount, and the Dividend Allowance when structuring their investment. Optimising timing across tax years may be beneficial.",
    tag: "Allowances",
    tagColor: "#C9A84C",
  },
  {
    icon: "ri-global-line",
    title: "Non-UK Residents & International Investors",
    desc: "The tax implications for non-UK resident investors differ significantly. Withholding tax may apply on distributions depending on applicable double tax treaties. Investors based in the UAE, GCC, or other jurisdictions should seek local tax advice as well as UK-specific guidance.",
    tag: "International",
    tagColor: "#9B7B4A",
  },
  {
    icon: "ri-building-2-line",
    title: "SDLT & Property-Level Taxation",
    desc: "Stamp Duty Land Tax (SDLT) and any other property-level taxes are borne by the SPV at the time of property acquisition and are accounted for within the Use of Funds allocation. Investors are not directly liable for property transaction taxes.",
    tag: "SDLT",
    tagColor: "#4CAF8A",
  },
  {
    icon: "ri-file-shield-2-line",
    title: "Tax Efficiency of the Structure",
    desc: "The SPV and lease structure has been designed with tax efficiency in mind. Rental income flows through the structure to service investor distributions in a manner intended to minimise unnecessary tax friction. Full details of the structural tax position are available in the complete Information Memorandum.",
    tag: "Structure",
    tagColor: "#C9A84C",
  },
];

export default function TaxConsiderationsSection() {
  return (
    <section className="bg-white py-20 md:py-24">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div>
            <span className="text-[#C9A84C] text-xs font-semibold uppercase tracking-widest block mb-3">
              Investor Guidance
            </span>
            <h2
              className="text-3xl md:text-4xl font-bold text-[#071A2E] leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Tax Considerations
            </h2>
            <p className="text-slate-500 text-sm mt-3 max-w-lg">
              The structure is designed to be tax efficient. However, individual tax treatment depends on personal circumstances. The following considerations apply.
            </p>
          </div>
          <div className="flex items-center gap-2 bg-[#EEF2F9] border border-[#D4E0ED] rounded-xl px-5 py-3 flex-shrink-0">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-error-warning-line text-[#C9A84C] text-sm leading-none" />
            </div>
            <p className="text-slate-600 text-xs">
              <strong className="text-[#071A2E]">Always seek independent tax advice</strong> before investing.
            </p>
          </div>
        </div>

        {/* Tax items grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {taxItems.map((item) => (
            <div
              key={item.title}
              className="bg-[#F5F8FC] border border-[#E0E8F0] rounded-2xl p-6 hover:border-[#C9A84C]/40 transition-all duration-200 group"
            >
              <div className="flex items-start justify-between gap-3 mb-4">
                <div
                  className="w-11 h-11 flex items-center justify-center rounded-xl flex-shrink-0"
                  style={{ backgroundColor: `${item.tagColor}18` }}
                >
                  <i
                    className={`${item.icon} text-lg leading-none`}
                    style={{ color: item.tagColor }}
                  />
                </div>
                <span
                  className="text-xs font-bold px-3 py-1 rounded-full flex-shrink-0"
                  style={{ backgroundColor: `${item.tagColor}18`, color: item.tagColor }}
                >
                  {item.tag}
                </span>
              </div>
              <h3
                className="font-bold text-[#071A2E] text-sm mb-3 leading-snug"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {item.title}
              </h3>
              <p className="text-slate-500 text-xs leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>

        {/* Disclaimer Box */}
        <div
          className="relative rounded-3xl overflow-hidden p-8 md:p-10"
          style={{
            background: "linear-gradient(135deg, #071A2E 0%, #0D2B4A 60%, #071A2E 100%)",
          }}
        >
          {/* Gold top border */}
          <div
            className="absolute top-0 left-0 right-0 h-0.5"
            style={{ background: "linear-gradient(90deg, transparent, #C9A84C, transparent)" }}
          />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#C9A84C]/20 text-[#C9A84C] flex-shrink-0">
                  <i className="ri-scales-3-line text-lg leading-none" />
                </div>
                <h3
                  className="font-bold text-white text-lg"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Independent Tax Advice Required
                </h3>
              </div>
              <p className="text-slate-300 text-sm leading-relaxed mb-4">
                Thorpe Dellow does not provide tax advice. The information above is for general guidance only and does not constitute personalised tax advice. Tax treatment depends on individual circumstances and may be subject to change. The tax efficiency of this structure is not guaranteed.
              </p>
              <p className="text-slate-400 text-xs leading-relaxed">
                Investors are strongly advised to consult a qualified tax adviser — such as a Chartered Tax Adviser (CTA) or Chartered Accountant (CA) — before committing capital to this product. In particular, investors with significant income, existing capital gains, pension arrangements, or international tax exposure should seek specialist advice.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <Link
                to="/contact"
                className="w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-[#C9A84C] text-[#071A2E] text-sm font-bold hover:bg-[#B8953B] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-customer-service-2-line leading-none" />
                Speak to Our Team
              </Link>
              <a
                href="https://portal.thorpedellow.com"
                target="_blank"
                rel="nofollow noreferrer"
                className="w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl border border-white/20 text-white text-sm font-semibold hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-external-link-line leading-none" />
                Access Your Tax Documents
              </a>
              <p className="text-slate-500 text-xs text-center leading-relaxed">
                Annual tax summaries and distribution statements available in the Client Portal.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
